import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link, 
  Divider, 
  IconButton, 
  Button,
  Paper,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  ArrowUpward,
  Business,
  Security,
  Support,
  Public,
  Verified,
  Copyright,
  PolicyOutlined,
  DescriptionOutlined,
  Launch
} from '@mui/icons-material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import logo from '../images/company_logo.png';
import logo_dark from '../images/company_logo_dark.png';
import bitnox_logo from '../images/bitknox_white_logo.png';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../contexts/ThemeContext';

function Footer() {
  const navigate = useNavigate();
  const { isLight } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const quickLinks = [
    { name: 'Why Alphanetix?', href: '#introduction', icon: <Business sx={{ fontSize: '1rem' }} /> },
    { name: 'Solutions', href: '#advantages', icon: <Support sx={{ fontSize: '1rem' }} /> },
    { name: 'Deployment Options', href: '#deployment', icon: <Public sx={{ fontSize: '1rem' }} /> },
    { name: 'How It Works', href: '#how-it-works', icon: <Verified sx={{ fontSize: '1rem' }} /> },
    { name: 'Contact Us', href: '#contact', icon: <Phone sx={{ fontSize: '1rem' }} /> }
  ];

  const solutions = [
    { name: 'FinTech Solutions', href: '#industry-focus' },
    { name: 'Healthcare Integration', href: '#industry-focus' },
    { name: 'Enterprise Solutions', href: '#industry-focus' },
    { name: 'Custom Development', href: '#contact' },
    { name: 'API Integration', href: '#how-it-works' }
  ];

  const locations = [
    {
      country: 'Pakistan',
      city: 'Lahore',
      details: 'Development Hub',
      icon: 'PK'
    },
    {
      country: 'United States', 
      city: 'New Jersey',
      details: 'Sales & Support',
      icon: 'US'
    },
    {
      country: 'Saudi Arabia',
      city: 'Riyadh', 
      details: 'Regional Office',
      icon: 'KSA'
    }
  ];

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        background: isLight 
          ? 'rgba(255, 255, 255, 0.4)'
          : `linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 100%)`,
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
        color: isLight ? '#1e293b' : 'white',
        pt: { xs: 6, md: 8 },
        pb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isLight
            ? `
              radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.04) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.03) 0%, transparent 50%),
              repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 199px,
                rgba(99, 102, 241, 0.03) 200px,
                rgba(99, 102, 241, 0.03) 201px
              )
            `
            : `
              radial-gradient(circle at 20% 50%, rgba(56, 189, 248, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
              repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 199px,
                rgba(255, 255, 255, 0.015) 200px,
                rgba(255, 255, 255, 0.015) 201px
              )
            `,
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            component="img"
            src={isLight ? logo_dark : logo}
            alt="Alphanetix Logo"
            sx={{ 
              height: { xs: 40, md: 48 }, 
              mb: 2,
              filter: isLight
                ? 'brightness(0) saturate(100%) invert(24%) sepia(95%) saturate(4839%) hue-rotate(234deg) brightness(96%) contrast(93%)'
                : 'none'
            }}
          />

          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 1,
              fontSize: { xs: '1.3rem', md: '1.6rem' },
              color: isLight ? '#1e293b' : '#ffffff',
            }}
          >
            Transform Your Business
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: isLight ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.7)',
              maxWidth: '500px',
              mx: 'auto',
              fontSize: '0.85rem',
            }}
          >
            Enterprise iPaaS — no-code integration, API management & workflow automation.
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(24px)',
                border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography 
                variant="h6" 
                component="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: isLight ? '#4f46e5' : '#38bdf8',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Business sx={{ mr: 1, fontSize: '1.3rem' }} />
                Contact Info
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: isLight ? 'rgba(255, 255, 255, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                      color: isLight ? '#6366f1' : '#38bdf8',
                      mr: 2
                    }}
                  >
                    <Email sx={{ fontSize: '1rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <Link 
                        href="mailto:contact@alphanetix.io" 
                        color="inherit" 
                        underline="hover"
                        sx={{ '&:hover': { color: isLight ? '#6366f1' : '#10b981' } }}
                      >
                        contact@alphanetix.io
                      </Link>
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: isLight ? 'rgba(255, 255, 255, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                      color: isLight ? '#6366f1' : '#0ea5e9',
                      mr: 2
                    }}
                  >
                    <Phone sx={{ fontSize: '1rem' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)', fontSize: '0.75rem' }}>
                      Toll Free
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <Link 
                        href="tel:+18553141529" 
                        color="inherit" 
                        underline="hover"
                        sx={{ '&:hover': { color: isLight ? '#6366f1' : '#0ea5e9' } }}
                      >
                        (855) 314-1529
                      </Link>
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  href="#contact"
                  sx={{
                    mt: 2,
                    borderColor: isLight ? 'rgba(30, 41, 59, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                    color: isLight ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: isLight ? '#6366f1' : '#10b981',
                      background: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      color: isLight ? '#6366f1' : '#10b981'
                    }
                  }}
                >
                  All Contact Options
                </Button>
              </Stack>
            </Paper>
          </Grid>
          
          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                background: isLight ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(24px)',
                border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography 
                variant="h6" 
                component="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: isLight ? '#4f46e5' : '#0ea5e9',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Support sx={{ mr: 1, fontSize: '1.3rem' }} />
                Quick Links
              </Typography>
              
              <Stack spacing={1.5}>
                {quickLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.href}
                    color="inherit"
                    underline="none"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: isLight ? '#6366f1' : '#0ea5e9',
                        background: isLight ? 'rgba(99, 102, 241, 0.06)' : 'rgba(139, 92, 246, 0.1)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        background: isLight ? 'rgba(231, 231, 231, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                        color: isLight ? '#6366f1' : '#0ea5e9',
                        mr: 2
                      }}
                    >
                      {link.icon}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {link.name}
                    </Typography>
                  </Link>
                ))}
              </Stack>
            </Paper>
          </Grid>
          
          {/* Solutions */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(24px)',
                border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography 
                variant="h6" 
                component="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: isLight ? '#4f46e5' : '#818cf8',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Public sx={{ mr: 1, fontSize: '1.3rem' }} />
                Solutions
              </Typography>
              
              <Stack spacing={1.5}>
                {solutions.map((solution, index) => (
                  <Link
                    key={index}
                    href={solution.href}
                    color="inherit"
                    underline="none"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: isLight ? '#6366f1' : '#818cf8',
                        background: isLight ? 'rgba(99, 102, 241, 0.06)' : 'rgba(139, 92, 246, 0.1)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {solution.name}
                    </Typography>
                  </Link>
                ))}
              </Stack>
            </Paper>
          </Grid>
          
          {/* Locations */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                background: isLight ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(24px)',
                border: isLight ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <Typography 
                variant="h6" 
                component="h3" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  color: isLight ? '#4f46e5' : '#f59e0b',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <LocationOn sx={{ mr: 1, fontSize: '1.3rem' }} />
                Global Offices
              </Typography>
              
              <Stack spacing={2}>
                {locations.map((location, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="span"
                      sx={{
                        mr: 2,
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        letterSpacing: '0.05em',
                        color: isLight ? '#6366f1' : '#38bdf8',
                        background: isLight ? 'rgba(99,102,241,0.08)' : 'rgba(56,189,248,0.1)',
                        borderRadius: '6px',
                        px: 1,
                        py: 0.4,
                        minWidth: 32,
                        textAlign: 'center',
                        lineHeight: 1.4,
                      }}
                    >
                      {location.icon}
                    </Typography>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: isLight ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)' 
                        }}
                      >
                        {location.city}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isLight ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.75rem'
                        }}
                      >
                        {location.details}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Partner Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 4
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 3,
              py: 2,
              background: isLight 
                ? 'rgba(255, 255, 255, 0.25)'
                : 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              border: isLight 
                ? '1px solid rgba(30, 41, 59, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              '&:hover': {
                background: isLight 
                  ? 'rgba(255, 255, 255, 0.4)'
                  : 'rgba(255, 255, 255, 0.08)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: isLight ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.85rem',
                fontWeight: 500
              }}
            >
              Sales & Support:
            </Typography>
            <Button
              component="a"
              href="https://bitknoxltd.com" 
              target="_blank" 
              rel="noopener"
              size="small"
              endIcon={<Launch sx={{ fontSize: '0.8rem' }} />}
              sx={{
                minHeight: 'auto',
                py: 0.5,
                px: 2,
                background: isLight ? 'rgba(99, 102, 241, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                color: isLight ? '#6366f1' : '#10b981',
                borderRadius: 2,
                fontSize: '0.8rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  background: isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                  color: isLight ? '#4f46e5' : '#059669'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <img 
                src={bitnox_logo} 
                alt="BitKnox Ltd" 
                style={{ height: 18, marginRight: 6 }}
              />
              BitKnox Ltd
            </Button>
          </Paper>
        </Box>
        
        {/* Footer Bottom */}
        <Divider sx={{ mb: 4, borderColor: isLight ? 'rgba(30, 41, 59, 0.1)' : 'rgba(255, 255, 255, 0.1)' }} />
        
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Copyright sx={{ fontSize: '1rem', mr: 1, color: isLight ? 'rgba(30, 41, 59, 0.6)' : 'rgba(255, 255, 255, 0.6)' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: isLight ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.9rem'
              }}
            >
              {new Date().getFullYear()} Alphanetix. All Rights Reserved.
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              component="button"
              onClick={() => navigate('/privacy-policy')}
              startIcon={<PolicyOutlined sx={{ fontSize: '1rem' }} />}
              sx={{
                color: isLight ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.85rem',
                textTransform: 'none',
                minWidth: 'auto',
                '&:hover': { color: isLight ? '#6366f1' : '#10b981' }
              }}
            >
              Privacy Policy
            </Button>
            
            <Button
              component="button"
              onClick={() => navigate('/terms-of-service')}
              startIcon={<DescriptionOutlined sx={{ fontSize: '1rem' }} />}
              sx={{
                color: isLight ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                fontSize: '0.85rem',
                textTransform: 'none',
                minWidth: 'auto',
                '&:hover': { color: isLight ? '#6366f1' : '#10b981' }
              }}
            >
              Terms of Service
            </Button>

            <IconButton
              component="a"
              href="https://www.linkedin.com/company/alphanetix/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Alphanetix on LinkedIn"
              size="small"
              sx={{
                color: isLight ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.6)',
                border: isLight ? '1px solid rgba(30,41,59,0.15)' : '1px solid rgba(255,255,255,0.12)',
                borderRadius: '8px',
                p: 0.75,
                transition: 'all 0.25s ease',
                '&:hover': {
                  color: '#0077b5',
                  borderColor: '#0077b5',
                  background: 'rgba(0, 119, 181, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 119, 181, 0.2)',
                }
              }}
            >
              <LinkedInIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Stack>
        </Box>
      </Container>

      {/* Back to Top Button */}
      {showBackToTop && (
        <Fade in timeout={300}>
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 30,
              right: 30,
              width: 60,
              height: 60,
              background: isLight
                ? 'rgba(99, 102, 241, 0.85)'
                : 'linear-gradient(135deg, #38bdf8 0%, #10b981 100%)',
              backdropFilter: 'blur(20px)',
              color: 'white',
              boxShadow: isLight
                ? '0 8px 25px rgba(99, 102, 241, 0.4)'
                : '0 8px 25px rgba(16, 185, 129, 0.4)',
              zIndex: 1000,
              '&:hover': {
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)'
                  : 'linear-gradient(135deg, #059669 0%, #0284c7 100%)',
                transform: 'translateY(-4px) scale(1.05)',
                boxShadow: isLight
                  ? '0 12px 35px rgba(99, 102, 241, 0.5)'
                  : '0 12px 35px rgba(16, 185, 129, 0.5)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            <ArrowUpward sx={{ fontSize: '1.5rem' }} />
          </IconButton>
        </Fade>
      )}
    </Box>
  );
}

export default Footer;